import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { ShieldAlert, ClipboardCheck, RefreshCw, ChevronRight, PlayCircle } from 'lucide-react';
import { LIVELLO_STILE, LivelloBadge, AlertRow } from '@/components/alertStyles';

const CellaScala = ({ s }) => {
  if (s.mancante) return <span className="text-[11px] text-slate-400">-</span>;
  const stile = LIVELLO_STILE[s.livello] || LIVELLO_STILE.verde;
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`text-sm font-semibold ${stile.text}`}>{s.punteggio}<span className="text-[10px] text-slate-400">/{s.punteggio_max}</span></span>
      <span className="text-[10px] text-slate-500">{s.classe}</span>
      {s.scaduta && <Badge className="text-[9px] bg-orange-100 text-orange-700 w-fit">da rivalutare</Badge>}
    </div>
  );
};

export default function ScaleAlert() {
  const navigate = useNavigate();
  const [riepilogo, setRiepilogo] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [soloAttivi, setSoloAttivi] = useState(true);
  const [loading, setLoading] = useState(true);
  const [espanso, setEspanso] = useState(null);

  const carica = useCallback((attivi = soloAttivi) => {
    setLoading(true);
    Promise.all([
      api.get('/scale/riepilogo'),
      api.get('/alert/riepilogo', { params: { solo_attivi: attivi } }),
    ]).then(([s, a]) => { setRiepilogo(s.data); setAlerts(a.data); })
      .catch((e) => { console.error('Errore caricamento scale e alert:', e); toast.error('Errore nel caricamento'); })
      .finally(() => setLoading(false));
  }, [soloAttivi]);

  useEffect(() => { carica(); }, [carica]);

  if (loading && !riepilogo) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;

  const tipi = Object.keys(riepilogo?.scale || {});

  return (
    <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="scale-alert-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Scale Valutative e Alert Clinici</h1>
          <p className="text-sm text-slate-500 mt-0.5">Conley, Braden, Barthel, MNA-SF e motore di sorveglianza a regole cliniche</p>
        </div>
        <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="gap-2" onClick={() => carica()} data-testid="ricarica-btn">
          <RefreshCw className="w-3.5 h-3.5" />Aggiorna
        </Button>
        <Button size="sm" className="gap-2 bg-brand hover:bg-brand-dark text-white" onClick={() => navigate('/giro-scale')} data-testid="avvia-giro-scale-btn">
          <PlayCircle className="w-4 h-4" />Avvia giro scale
        </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50/30"><CardContent className="p-4">
          <p className="text-xs text-red-600 font-medium uppercase tracking-wider">Ospiti critici</p>
          <p className="text-3xl font-bold text-red-700 font-[Cinzel]" data-testid="kpi-rossi">{alerts?.ospiti_rossi || 0}</p>
        </CardContent></Card>
        <Card className="border-amber-200 bg-amber-50/30"><CardContent className="p-4">
          <p className="text-xs text-amber-600 font-medium uppercase tracking-wider">Ospiti in attenzione</p>
          <p className="text-3xl font-bold text-amber-700 font-[Cinzel]" data-testid="kpi-gialli">{alerts?.ospiti_gialli || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Copertura scale</p>
          <p className="text-3xl font-bold text-slate-900 font-[Cinzel]" data-testid="kpi-copertura">{riepilogo?.copertura_pct || 0}%</p>
          <p className="text-[11px] text-slate-400">{riepilogo?.contatori?.complete || 0} ospiti su {riepilogo?.totale_ospiti || 0} completi</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Da compilare / scadute</p>
          <p className="text-3xl font-bold text-slate-900 font-[Cinzel]" data-testid="kpi-mancanti">{(riepilogo?.contatori?.mancanti || 0) + (riepilogo?.contatori?.scadute || 0)}</p>
          <p className="text-[11px] text-slate-400">{riepilogo?.contatori?.mancanti || 0} mai compilate · {riepilogo?.contatori?.scadute || 0} scadute</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="alert">
        <TabsList>
          <TabsTrigger value="alert" className="text-xs gap-1.5" data-testid="tab-alert"><ShieldAlert className="w-3.5 h-3.5" />Alert Clinici</TabsTrigger>
          <TabsTrigger value="scale" className="text-xs gap-1.5" data-testid="tab-scale"><ClipboardCheck className="w-3.5 h-3.5" />Riepilogo Scale</TabsTrigger>
        </TabsList>

        <TabsContent value="alert" className="mt-4">
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-800">Sorveglianza clinica ({alerts?.alert_totali || 0} alert)</CardTitle>
              <label className="flex items-center gap-2 text-xs text-slate-500">
                Solo ospiti con alert
                <Switch checked={soloAttivi} onCheckedChange={(v) => { setSoloAttivi(v); carica(v); }} data-testid="toggle-solo-attivi" />
              </label>
            </CardHeader>
            <CardContent className="space-y-2">
              {(alerts?.ospiti || []).length === 0 && <p className="text-sm text-slate-500 py-6 text-center">Nessun alert clinico attivo.</p>}
              {(alerts?.ospiti || []).map(o => {
                const stile = LIVELLO_STILE[o.livello] || LIVELLO_STILE.verde;
                const aperto = espanso === o.ospite_id;
                return (
                  <div key={o.ospite_id} className={`rounded-lg border ${stile.card}`} data-testid={`alert-ospite-riga-${o.ospite_id}`}>
                    <div className="flex items-center justify-between gap-3 p-3 cursor-pointer" onClick={() => setEspanso(aperto ? null : o.ospite_id)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${stile.dot}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{o.ospite_nome}</p>
                          <p className="text-[11px] text-slate-400">Camera {o.stanza || '-'}/{o.letto || '-'} · {o.area || 'N/D'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {o.news2 !== null && o.news2 !== undefined && <Badge variant="outline" className="text-[10px]">NEWS2 {o.news2}</Badge>}
                        {o.rossi > 0 && <Badge className="text-[10px] bg-red-100 text-red-700">{o.rossi} critic{o.rossi === 1 ? 'o' : 'i'}</Badge>}
                        {o.gialli > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-700">{o.gialli} attenzione</Badge>}
                        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${aperto ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                    {aperto && (
                      <div className="px-3 pb-3 space-y-2">
                        {o.alert.map((a, i) => <AlertRow key={i} a={a} testid={`alert-dett-${o.ospite_id}-${a.codice}`} />)}
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => navigate(`/ospiti/${o.ospite_id}`)}>
                          Apri scheda ospite
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scale" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-800">Scale per ospite</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ospite</TableHead>
                    <TableHead>Camera</TableHead>
                    {tipi.map(t => <TableHead key={t} className="text-xs">{riepilogo.scale[t].nome}</TableHead>)}
                    <TableHead>Rischio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(riepilogo?.ospiti || []).map(o => (
                    <TableRow key={o.ospite_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ospiti/${o.ospite_id}`)} data-testid={`scale-riga-${o.ospite_id}`}>
                      <TableCell className="text-sm font-medium text-slate-800">{o.ospite_nome}</TableCell>
                      <TableCell className="text-xs text-slate-500">{o.stanza || '-'}/{o.letto || '-'}</TableCell>
                      {tipi.map(t => <TableCell key={t}><CellaScala s={o.scale[t]} /></TableCell>)}
                      <TableCell><LivelloBadge livello={o.livello_peggiore} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
